/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from 'primereact/button';
import {
  CartItemInput,
  CartItemInterface,
  ConfigurableVariant,
  useAddProductsToCartMutation,
} from 'graphql/generated/magentoApi';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { rootPath } from 'pages/ProductList';
import { showErrorToast } from 'utils/errorHelpers';
import { Toast } from 'primereact/toast';
import { useCartContext } from 'CartContext';
import {
  useNavigate,
  Link,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import parse from 'html-react-parser';
import ReactGA from 'react-ga4';
import Stars from 'components/Stars';
import './ProductDescription.scss';
import { ProductWithVariants } from 'types';

type Props = {
  product: ProductWithVariants;
  fromIMedia?: boolean;
};

export default function ProductDescription({
  product,
  fromIMedia = false,
}: Props): ReactElement {
  const {
    __typename,
    name,
    short_description,
    rating_summary,
    price_range,
    categories,
    is_subscription,
    vendor_company,
  } = product;

  const filteredCategories =
    categories?.filter(category => {
      if (rootPath) {
        return (
          category?.url_path !== rootPath &&
          category?.url_path?.includes(rootPath)
        );
      }
      return true;
    }) || [];

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  const [finalPrice, setFinalPrice] = useState(
    price_range.maximum_price?.final_price.value
      ? formatter.format(price_range.maximum_price?.final_price.value)
      : price_range.maximum_price?.final_price.value,
  );
  const pricingText =
    is_subscription === 1 ? `${finalPrice}/mo` : `${finalPrice}`;

  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [addProductState, addProducts] = useAddProductsToCartMutation();
  const { cartId, updateItems, reset, getProductWebsiteUrl } = useCartContext();
  const [websiteUrl, setWebsiteUrl] = useState(() =>
    getProductWebsiteUrl(product),
  );
  const [variant, setVariant] = useState<ConfigurableVariant>();
  const [variants, setVariants] = useState<ConfigurableVariant[]>([]);
  const { sku } = useParams();
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('isEmbedded') || undefined;

  const toast = useRef<Toast>(null);

  useEffect(() => {
    if (product.variants?.length) {
      setVariants(product.variants);
    }
  }, []);

  useEffect(() => {
    setWebsiteUrl(() => getProductWebsiteUrl(product));
  }, [sku, product]);

  useEffect(() => {
    if (variant) {
      setFinalPrice(
        formatter.format(
          variant.product.price_range.minimum_price.regular_price.value,
        ),
      );
    } else {
      setFinalPrice(
        price_range.maximum_price?.final_price.value
          ? formatter.format(price_range.maximum_price?.final_price.value)
          : price_range.maximum_price?.final_price.value,
      );
    }
  }, [variant, price_range, formatter]);

  useEffect(() => {
    const items = addProductState.data?.addProductsToCart?.cart.items as
      | CartItemInterface[]
      | undefined;
    if (!addProductState.fetching && items?.length) {
      updateItems([...items]);
      setLoading(false);
      if (!fromIMedia) {
        navigate('/cart');
      }
    } else if (addProductState.error) {
      setLoading(false);
      showErrorToast(toast.current, addProductState.error);
      reset();
    }
  }, [addProductState, fromIMedia, loading, navigate, reset, updateItems]);

  const onClickAddBtn = () => {
    setLoading(true);
    addProducts({
      cartId,
      cartItems: [
        {
          sku: product.sku,
          quantity: __typename === 'VirtualProduct' ? 1 : quantity,
        },
      ] as CartItemInput[],
    });

    ReactGA.event({
      category: 'ecommerce',
      action: 'add_to_cart',
      label: `Product Description Action ${name} - ${product.sku}`,
    });

    toast.current?.show({
      severity: 'success',
      summary: 'Success',
      detail: 'Added to Cart',
      contentClassName: '',
    });
  };

  const onClickViewCartBtn = () => {
    navigate('/cart');
    ReactGA.event({
      category: 'ecommerce',
      action: 'View Cart',
      label: `View Cart ${name} - ${product.sku}`,
    });
  };

  const onClickVisitWebsiteBtn = (url: string) => {
    window.open(url, '_blank');
    ReactGA.event({
      category: 'external_link',
      action: 'Visit Website - Product Description',
      label: `Visited ${url}`,
    });
    setWebsiteUrl(() => getProductWebsiteUrl(product));
  };

  useEffect(() => {
    const QuantityButtons = Array.from(
      document.querySelectorAll('.p-inputnumber-button'),
    );
    QuantityButtons.forEach(QuantityButton => {
      QuantityButton.setAttribute('aria-label', 'Change Quantity');
    });
  });

  useEffect(() => {
    const QuantityInputs = Array.from(
      document.querySelectorAll(
        '.p-inputtext.p-component.p-filled.p-inputnumber-input',
      ),
    );
    QuantityInputs.forEach(QuantityInput => {
      QuantityInput.setAttribute('aria-label', 'Change Quantity Input');
    });
  });

  const selectedVariantTemplate = (
    option: ConfigurableVariant,
    props: { placeholder: string },
  ) => {
    if (option) {
      return <div>{option.attributes[0].label}</div>;
    }
    return <span>{props.placeholder}</span>;
  };

  const variantOptionTemplate = (option: ConfigurableVariant) => (
    <div>{`${option.attributes[0].label} / $${option.product.price_range.minimum_price.regular_price.value}`}</div>
  );

  return (
    <div className="product-description__container relative">
      <h1 className="font-normal mt-3 mb-2 h2-product-name">{name}</h1>
      {!!rating_summary && (
        <Stars rating={(rating_summary * 5) / 100} classNames="mb-2" />
      )}
      <p className="font-medium mt-0 mb-2 text-2xl p-pricing-text">
        {`${pricingText === '0' ? 'Free' : `${pricingText}`}`}
      </p>
      <div className="flex flex-column xl:flex-row align-items-start pb-2 mt-4">
        {__typename !== 'VirtualProduct' && (
          <InputNumber
            className="mr-3 c-button-gray"
            value={quantity}
            onValueChange={e => setQuantity(e.value)}
            showButtons
            buttonLayout="horizontal"
            incrementButtonIcon="pi pi-plus"
            aria-label="Adjust product quantity"
            decrementButtonIcon="pi pi-minus"
            min={1}
            max={99}
          />
        )}
        {!!variants.length && (
          <Dropdown
            className="w-8 sm:w-6 mr-3 mt-3 xl:mt-0"
            value={variant}
            options={product.variants}
            onChange={e => setVariant(e.value as ConfigurableVariant)}
            optionLabel="variant"
            placeholder="Select a license type"
            valueTemplate={selectedVariantTemplate}
            itemTemplate={variantOptionTemplate}
          />
        )}
      </div>
      <div className="flex flex-column xl:flex-row align-items-start pb-2 mt-0">
        <Button
          className={`uppercase my-3 xl:mt-0 text-sm font-bold ${
            fromIMedia
              ? 'p-button-rounded add-to-cart-button'
              : 'border-noround c-button-primary mt-3'
          }`}
          label="Add to Cart"
          onClick={() => onClickAddBtn()}
          loading={loading}
          type="button"
          id="product-description-add-to-cart"
          disabled={variants.length ? !variant : false}
        />
        {!isEmbedded && (
          <>
            {websiteUrl && (
              <Button
                className="uppercase mt-3 xl:mt-0 xl:ml-3 border-noround c-button-primary mt-3 text-sm font-bold"
                label="Visit Website"
                onClick={() => onClickVisitWebsiteBtn(websiteUrl)}
                type="button"
              />
            )}
          </>
        )}
      </div>
      <div className="flex flex-column xl:flex-row align-items-start pb-2 mt-0">
        {fromIMedia && (
          <Button
            className="uppercase view-cart-button ml-0 my-2 md:mt-0 text-center"
            label="View cart"
            onClick={() => onClickViewCartBtn()}
            id="product-description-view-cart"
          />
        )}
      </div>
      <p className="mt-0 mb-2 product-categories">
        <span className="font-bold">Categories: </span>
        {filteredCategories?.map(category => (
          <Link
            className="ml-1 category-link"
            to={`/category/${category?.url_path}`}
            key={category?.name}
          >
            {category?.name}
          </Link>
        ))}
      </p>
      <div className="text-sm p_m-0 product-short-description">
        {short_description?.html && parse(short_description.html)}
        <p className="mt-0 mb-2 p-product-vendor">
          <span className="font-bold">Vendor: </span>
          <Link
            className="ml-1 category-link"
            to={`/category?vendor=${vendor_company}`}
            key={vendor_company}
          >
            {vendor_company}
          </Link>
        </p>
      </div>
      {is_subscription === 1 && (
        <p className="mt-0 mb-3">
          <span className="font-bold">Subscription: </span>
          {`${finalPrice} a Month`}
        </p>
      )}
      <Toast
        ref={toast}
        position="top-right"
        className="product-description__toast"
      />
    </div>
  );
}
